<template>
    <div class="flex flex-col bg-base" id="layout-default">
        <a
            href="#main-content"
            class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-white focus:px-5 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-gray-900 focus:shadow-lg focus:outline-2 focus:outline-primary-500 dark:focus:bg-surface-card dark:focus:text-txt-headings"
            @click.prevent="skipToContent"
        >
            Preskočiť na obsah
        </a>

        <div class="flex min-h-screen flex-col bg-base">
            <HeaderNew class="flex-none" />

            <CAlert v-if="infoAlertMessage" center full-width variant="info">
                <span v-html="infoAlertMessage"></span>
            </CAlert>

            <main
                id="main-content"
                tabindex="-1"
                class="flex-auto focus:outline-none"
                :class="{
                    'container flex-auto pt-6 pb-12 md:pt-10 md:pb-16': enableContainer
                }"
            >
                <slot />
            </main>

            <AppFooter v-if="isFooterVisible" />
        </div>

        <ClientOnly>
            <CookieBanner />
        </ClientOnly>

        <!-- Platform-wide chat overlay - only show when logged in. Hidden (not unmounted) on the full chat
             page: unmounting stops its presence tracking and could mark the user offline there. -->
        <ClientOnly>
            <ChatOverlay v-if="isLoggedIn" v-show="!isChatsPage" />
        </ClientOnly>

        <!-- Voice Agent FAB - temporarily disabled (not working, to be fixed later).
             When re-enabling, load it with defineAsyncComponent like ChatOverlay.
        <ClientOnly>
            <VoiceAgentFab v-if="isLoggedIn" />
        </ClientOnly>
        -->

        <ClientOnly>
            <Teleport to="body" />
        </ClientOnly>

        <!-- Modal renderer for useModalStore -->
        <ClientOnly>
            <template v-for="modal in modalStore.modalStack" :key="modal.id">
                <component
                    v-if="modal.component"
                    :is="modal.component"
                    v-bind="modal.props"
                    @close="(data: any) => modalStore.closeModal(data)"
                />
            </template>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import type { UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import AppFooter from '@/components/footer/AppFooter.vue'
import HeaderNew from '@/components/header/HeaderNew.vue'
import CAlert from '@/components/ui/CAlert.vue'
import { useGrowniGtag } from '@/composables/analytics/gtag'
import { useMetaPixel } from '@/composables/analytics/metaPixel'
import { useApplicationStore } from '@/stores/application'
import { useModalStore } from '@/stores/modal'

// Signed-in only: loaded on demand so anonymous visitors don't download the chat code.
const ChatOverlay = defineAsyncComponent(() => import('@/components/chat/ChatOverlay.vue'))

const route = useRoute()

useGrowniGtag()
useMetaPixel()
const store = useApplicationStore()
const modalStore = useModalStore()

const isLoggedIn = computed(() => store.isLoggedIn)
const infoAlertMessage = ref('')

const isFooterVisible = computed(() => !['messages', 'messages-id'].includes(route.name as string))

const isChatsPage = computed(() => route.name === 'chats')

// Focus <main> without touching the URL hash, which the router would treat as a navigation
function skipToContent() {
    document.getElementById('main-content')?.focus()
}

const userProfile = computed<UserProfile | undefined>(() => store.userProfile)

const _isOrganisation = computed(() => store.userOrgs?.length !== 0)

const _isMentor = computed(() => userProfile.value?.mentorProfile?.visibility === VisibilityType.published)

const _isVolunteer = computed(() => userProfile.value?.volunteerProfile?.visibility === VisibilityType.published)

const enableContainer = computed(() => {
    if (
        route.name === 'index' ||
        route.name === 'ako-pridat-pracovnu-ponuku' ||
        route.name === 'tyzdendobrovolnictva' ||
        // Renders its own container through OpportunityPageLayout, like the public job page.
        route.name === 'profile-activity-job-application-id'
    ) {
        return false
    }
    return true
})

const trackGtmEvent = ref(false)
const trackGtmCode = ref('')

useHead(() => {
    if (!trackGtmEvent.value) return {}

    return {
        script: [
            {
                innerHTML: `
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({${trackGtmCode.value}});
        `,
                type: 'text/javascript'
            }
        ]
    }
})

watch(route, () => {
    if (infoAlertMessage.value) {
        infoAlertMessage.value = ''
    }
})

// Provide functions globally to avoid redefinition errors
const addInfoMessage = (message: string) => {
    infoAlertMessage.value = message
}

// Kept for backward compatibility with pages that might still call these
const hideNavbarFunction = (value: boolean = true) => {
    // HeaderNew doesn't use this, but kept for compatibility
}

const setNavbarTheme = (theme: any) => {
    // HeaderNew doesn't use this, but kept for compatibility
}

provide('addInfoMessage', addInfoMessage)
provide('hideNavbar', hideNavbarFunction)
provide('navbarTheme', setNavbarTheme)

onMounted(() => {
    setTimeout(() => {
        trackGtmCode.value =
            `event: "user_info", user_id: ` +
            (userProfile.value ? `'${userProfile.value.id}'` : `"not_set"`) +
            `, is_mentor: ${_isMentor.value}, is_volunteer: ${_isVolunteer.value}` +
            ', is_organization: ' +
            _isOrganisation.value
        trackGtmEvent.value = true
    }, 1000)
})
</script>

<style lang="postcss">
html,
body,
#__nuxt,
#__layout {
    height: 100%;
}

/* Removed overly aggressive margin reset */

body {
    background-color: var(--color-bg-base);
    color: var(--color-txt-headings);
}
</style>
